import { Box, Typography } from '@mui/material';
import { EditableSectionContainer, EditableSectionRow, LoadableTypography, AddressSettingsRow, useAddressForm, PhoneSettingsRow, usePhoneForm, PhoneVerificationModal, Alert, LinkButton, CheckYourInboxDialog, VerificationInfoLabel } from '@uplift/ui-v2';
import { SettingsRowKey, SettingsSectionBaseProps } from '../types';
import { useSettingsStyles } from '../styles';
import { trackToDoCTA, useIsMobile, useL10n } from '@uplift/hooks';
import { useCallback, useState } from 'react';
import { useCurrentuser } from '@uplift/contexts';
import { useQueryClient } from '@tanstack/react-query';
import { ContactName, UserRole } from '@uplift/types';
import { ActionItemKeys, requestNewVerifyEmail } from '@uplift/api';

export const ContactSettingsSection: React.VFC<SettingsSectionBaseProps> = ({ userId, data, isLoading }) => {
  const { classes } = useSettingsStyles();
  const l10n = useL10n();
  const me = useCurrentuser();
  const queryClient = useQueryClient();
  const { phoneFormik } = usePhoneForm(
    userId,
    data?.contacts,
    true,
    async () => {
      if (data?.contacts?.mobileVerified === false) {
        await queryClient.invalidateQueries({ queryKey: [ 'user', userId, 'settings' ] });
        setPhoneVerificationModalOpen(true);
      }
    }
  );
  const { addressFormik } = useAddressForm(userId, data?.profile, l10n.providerSettings.homeAddressRequired);
  const isMobile = useIsMobile('sm');
  const disableEdit = isMobile && (data?.contacts?.mobileVerified === false) ? false : true;

  const [ phoneVerificationModalOpen, setPhoneVerificationModalOpen ] = useState(false);
  const [ emailVerificationModalOpen, setEmailVerificationModalOpen ] = useState(false);

  const handleVerifyPhone = useCallback(() => {
    setPhoneVerificationModalOpen(true);
  }, []);

  const handleVerifyPhoneClose = useCallback(async () => {
    setPhoneVerificationModalOpen(false);

    queryClient.invalidateQueries({ queryKey: [ 'user', userId, 'settings' ] });
  }, [ queryClient, userId ]);

  const handleEmailVerificationClose = () => {
    setEmailVerificationModalOpen(false);
  };

  const handleEmailVerificationOpen = useCallback(async () => {
    await requestNewVerifyEmail();
    trackToDoCTA({
      toDoType: ActionItemKeys.PROVIDER_VERIFY_ACCOUNT_EMAIL,
      userType: UserRole.provider
    });

    setEmailVerificationModalOpen(true);
  }, []);

  return (
    <EditableSectionContainer
      title={l10n.providerSettings.contactInformation}
      rowDividers
      formikConfigs={{
        [SettingsRowKey.address]: addressFormik,
        [SettingsRowKey.phone]: phoneFormik,
      }}
    >
      <AddressSettingsRow
        rowKey={SettingsRowKey.address}
        profile={data?.profile}
        states={data?.states}
      />

      <PhoneSettingsRow
        rowKey={SettingsRowKey.phone}
        contacts={data?.contacts}
        mobileVerified={data?.contacts?.mobileVerified}
        showVerifyAlert
        onVerifyClick={handleVerifyPhone}
      />

      <EditableSectionRow
        label={l10n.providerSettings.accountEmail}
        rowKey={SettingsRowKey.accountEmail}
        kind="custom"
      >
        <LoadableTypography
          className={classes.fontWeight500}
          width="140px"
          loading={isLoading}
        >
          <VerificationInfoLabel label={data?.user?.email} verified={data?.user?.emailVerified} />
        </LoadableTypography>
        {!data.user?.emailVerified && (
          <Box pt={1} pb={1} maxWidth="486px" width={isMobile ? 'initial' : 'calc(100% - 64px)'}>
            <Alert severity="warning">Unverified—add extra security and <LinkButton variant="body2" color="textPrimary" onClick={handleEmailVerificationOpen}>verify your email</LinkButton></Alert>
          </Box>
        )}
        <Box mt={0.5}>
          <Typography variant="body2">{l10n.providerSettings.needToChangeEmail}</Typography>
        </Box>
      </EditableSectionRow>
      <PhoneVerificationModal
        open={phoneVerificationModalOpen}
        onClose={handleVerifyPhoneClose}
        userId={me?.id}
        phone={data?.contacts[ContactName.mobile]}
        contactId={data?.contacts?.mobileContactId}
        contactName={ContactName.mobile}
        disableEdit={disableEdit}
        maskPhone
        title={l10n.phoneValidationModal.verifyYourNumber}
        successText={l10n.phoneValidationModal.mobileNumberVerified}
      />
      <CheckYourInboxDialog open={emailVerificationModalOpen} onClose={handleEmailVerificationClose} />
    </EditableSectionContainer>
  );
};
